import { useCallback, useState } from 'react'
import useDirectory from 'hooks/useDirectory'
import { useRecoilState } from 'recoil'
import { currentPovState } from 'store/krpanoStore'
import currentViewerState from 'store/viewerStore'

import Modal from 'components/atoms/Modal'
import Spinner from 'components/atoms/Spinner'
import Title from 'components/atoms/Title'
import CloseIcon from 'components/icons/CloseIcon'
import NoImageIcon from 'components/icons/NoImageIcon'
import PdfIcon from 'components/icons/PdfIcon'

// File type returned from Annotation Detail API (resources.FilePointResource)
export type AnnotationFileItem = {
  id: number
  s3Path?: string | null
  name: string
  extension: string
  mimeType: string
  thumbnail?: string | null
  pointId?: string | null
  buildingId?: number | null
}

type Props = {
  open: boolean
  onClose: () => void
  files: AnnotationFileItem[]
  buildingId?: number
  isLoading?: boolean
}

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']

function isImageFile(file: AnnotationFileItem) {
  return IMAGE_EXTENSIONS.includes((file.extension || '').toLowerCase())
}

// Thumbnail thông minh: chỉ load ảnh khi là image, còn lại hiện icon ngay
function FileThumbnail({ file }: { file: AnnotationFileItem }) {
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(false)

  const url = file.thumbnail || file.s3Path || ''

  // Mọi file không phải ảnh: hiển thị icon tức thì, KHÔNG dùng spinner
  if (!isImageFile(file)) {
    const ext = (file.extension || '').toLowerCase()
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-[#F0F2F5] rounded gap-1">
        {ext === 'pdf' ? (
          <PdfIcon />
        ) : (
          <div className="text-3xl font-bold text-gray-400 uppercase">
            {ext || '?'}
          </div>
        )}
        <div className="text-[10px] text-gray-400 truncate max-w-[90%]">
          {file.name}
        </div>
      </div>
    )
  }

  // Chỉ file ảnh mới load với spinner trong thời gian chờ
  return (
    <div className="w-full h-full relative bg-[#CED0D5] flex items-center justify-center">
      {!loaded && !error && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Spinner spinning>
            <div className="w-5 h-5" />
          </Spinner>
        </div>
      )}
      {error ? (
        <NoImageIcon width={40} height={40} />
      ) : (
        <img
          src={url}
          alt={file.name}
          className={`w-full h-full object-contain transition-opacity duration-300 ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setLoaded(true)}
          onError={() => {
            setLoaded(true)
            setError(true)
          }}
          draggable={false}
        />
      )}
    </div>
  )
}

export default function ModalAnnotationFiles({
  open,
  onClose,
  files,
  buildingId,
  isLoading
}: Props) {
  const { navigate } = useDirectory()
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const [, setCurrentPov] = useRecoilState(currentPovState)

  const handleMoveToPoint = useCallback(
    (pointId: string) => {
      if (!buildingId || !pointId) return
      currentViewer?.selectedPoint !== pointId && setCurrentPov(null)
      setCurrentViewer((prev) => ({
        ...prev,
        selectedPoint: pointId
      }))
      navigate(`/viewer/buildings/${buildingId}`)
    },
    [buildingId, currentViewer, navigate, setCurrentPov, setCurrentViewer]
  )

  if (!open) return null

  return (
    <Modal open width={700} centered>
      <div className="absolute top-5 right-5 cursor-pointer" onClick={onClose}>
        <CloseIcon />
      </div>
      <Title title="アノテーションのファイル一覧" />

      <div className="min-h-[470px] max-h-[470px] overflow-auto space-y-[10px]">
        {isLoading && (
          <Spinner spinning>
            <div className="h-10" />
          </Spinner>
        )}

        {!isLoading && files.length === 0 && (
          <div className="flex items-center justify-center h-40 text-gray-400">
            ファイルがありません
          </div>
        )}

        {!isLoading &&
          files.map((file) => (
            <div
              key={file.id}
              className={`flex gap-5 items-center ${
                file.pointId ? 'cursor-pointer' : ''
              }`}
              onClick={() => {
                if (file.pointId) {
                  handleMoveToPoint(file.pointId)
                }
              }}
            >
              {/* Thumbnail */}
              <div className="w-[220px] h-[110px] relative flex-shrink-0 rounded overflow-hidden">
                <FileThumbnail file={file} />
              </div>

              {/* File info */}
              <div>
                <div className="flex font-medium items-start pb-2">
                  <div className="flex-shrink-0">ファイル名：</div>
                  <div>{file.name}</div>
                </div>
                <div className="flex font-medium items-start">
                  <div className="flex-shrink-0">種類：</div>
                  <div className="uppercase">{file.extension}</div>
                </div>
                {file.pointId && (
                  <div className="mt-2 text-sm text-blue-600 underline">
                    ▶ 360°で見る
                  </div>
                )}
              </div>
            </div>
          ))}
      </div>
    </Modal>
  )
}

ModalAnnotationFiles.defaultProps = {
  buildingId: undefined,
  isLoading: false
}
